/**
 * Dynamic Statistical Trend Analysis System
 *
 * The statistical helpers, the TrendAlert type, and the core analyzer now live
 * in @livingsyncs/types so the per-resident panel, the nightly backend sweep
 * (trendAlertSweep.ts), and the server-computed /form-trends endpoint share one
 * implementation. This file keeps the frontend's historical export names.
 */

import { calculateStatistics } from "@livingsyncs/types";

// Re-export shared types so existing consumers don't need to change imports.
export type { FieldStatistics, TrendAlert } from "@livingsyncs/types";

// Backward-compatible name for the shared analyzer.
export { analyzeFormSubmissionTrends as analyzeResidentTrends } from "@livingsyncs/types";

export function getFieldStatistics(
  values: number[],
): import("@livingsyncs/types").FieldStatistics {
  return calculateStatistics(values);
}

export function detectMissingDataPatterns(
  expectedSubmissions: number,
  actualSubmissions: number,
  formName: string,
): import("@livingsyncs/types").TrendAlert | null {
  const missedPercentage =
    ((expectedSubmissions - actualSubmissions) / expectedSubmissions) * 100;

  if (missedPercentage > 30) {
    return {
      id: `missing-data-${Date.now()}`,
      residentId: "",
      residentName: "",
      severity: missedPercentage > 50 ? "warning" : "info",
      type: "missing_data",
      fieldName: "Form Submissions",
      formName,
      message: `Missing ${formName} submissions`,
      details: `Only ${actualSubmissions} of ${expectedSubmissions} expected submissions recorded (${missedPercentage.toFixed(0)}% missing)`,
      dataPoints: actualSubmissions,
      detectedAt: new Date(),
    };
  }

  return null;
}
